import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  VStack,
  HStack,
  Badge,
  Divider,
  Input,
} from "@chakra-ui/react";
import Pagination from "@olagg/ui-kit/Pagination";
import { AdminGameSeasonClawMachinePrizeClaim } from "@olagg/db-types";
import useClawPrizeClaims from "./hooks/useClawPrizeClaims";

const ClaimDetailsModal = ({ 
  isOpen, 
  onClose, 
  claim 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  claim: AdminGameSeasonClawMachinePrizeClaim;
}) => {
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Detalles del Claim #{claim.id}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Información del Usuario */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={2}>Información del Usuario</Text>
              <VStack spacing={2} align="stretch" bg="gray.700" p={3} borderRadius="md">
                <HStack justify="space-between">
                  <Text color="gray.400">ID:</Text>
                  <Text fontWeight="bold">{claim.userInfo.id}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.400">Username:</Text>
                  <Text fontWeight="bold">{claim.userInfo.username}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.400">Email:</Text>
                  <Text fontWeight="bold">{claim.userInfo.email}</Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Información del Play */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={2}>Información del Juego</Text>
              <VStack spacing={2} align="stretch" bg="gray.700" p={3} borderRadius="md">
                <HStack justify="space-between">
                  <Text color="gray.400">Play ID:</Text>
                  <Text fontWeight="bold">{claim.playInfo.id}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.400">Posición X:</Text>
                  <Text fontWeight="bold">{claim.playInfo.xPosition.toFixed(2)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.400">Posición Z:</Text>
                  <Text fontWeight="bold">{claim.playInfo.zPosition.toFixed(2)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.400">Cheat Code:</Text>
                  <Badge colorScheme={claim.playInfo.cheatCodeUsed ? "yellow" : "gray"}>
                    {claim.playInfo.cheatCodeUsed ? "Sí" : "No"}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.400">Jugado el:</Text>
                  <Text fontWeight="bold" fontSize="sm">{formatDate(claim.playInfo.playedAt)}</Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Fechas del Claim */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={2}>Fechas</Text>
              <VStack spacing={2} align="stretch" bg="gray.700" p={3} borderRadius="md">
                <HStack justify="space-between">
                  <Text color="gray.400">Reclamado el:</Text>
                  <Text fontWeight="bold" fontSize="sm">{formatDate(claim.claimedAt)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.400">Creado el:</Text>
                  <Text fontWeight="bold" fontSize="sm">{formatDate(claim.createdAt)}</Text>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ClaimActions = ({ claim }: { claim: AdminGameSeasonClawMachinePrizeClaim }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Button 
        colorScheme="blue"
        size="sm"
        onClick={onOpen}
      >
        Detalles
      </Button>
      
      <ClaimDetailsModal 
        isOpen={isOpen} 
        onClose={onClose} 
        claim={claim} 
      />
    </>
  );
};

const GameSeasonClawMachinePrizeClaims = () => {
  const { id: gameSeasonId, prize_id: prizeId } = useParams<{ id: string; prize_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getClawPrizeClaims, loading } = useClawPrizeClaims();
  const [claims, setClaims] = useState<AdminGameSeasonClawMachinePrizeClaim[]>([]);
  const [count, setCount] = useState(0);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filter, setFilter] = useState({
    query: searchParams.get('query') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 20),
  });
  const { page, limit, query } = filter;

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (name === 'page' || name === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue });

    if (value) searchParams.set(name, value);
    else searchParams.delete(name);
    setSearchParams(searchParams);
  };

  const onClearFilters = () => {
    setFilter({
      query: '',
      page: 1,
      limit: 20,
    });
    setSearchParams(new URLSearchParams());
  };

  const fetchClaims = async () => {
    console.log('Fetching claims with params:', { gameSeasonId, prizeId, page, limit, query: debouncedQuery });
    if (!gameSeasonId || !prizeId) return;
    
    try {
      const response = await getClawPrizeClaims({ 
        prizeId,
        limit, 
        page,
        query: debouncedQuery || undefined,
      });
      setClaims(response.clawMachinePrizeClaims);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching claims:', error);
    }
  };

  // Debounce query filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 500)

    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    fetchClaims();
  }, [page, limit, debouncedQuery, gameSeasonId, prizeId]);

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlQuery = searchParams.get('query');

    if (urlPage || urlLimit || urlQuery) {
      setFilter({
        query: urlQuery || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 20),
      });
    }
  }, []);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Claims del Premio de la Garra
        </Heading>

        {/* Filtro de búsqueda */}
        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Buscar por nombre, email o ID
            </Text>
            <Input
              placeholder="Buscar usuario..."
              value={query}
              onChange={(e) => onFilter({ name: 'query', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            />
          </Box>

          <Box>
            <Text color="transparent" fontSize="sm" mb={1}>
              .
            </Text>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        <Flex w='full' alignItems='center' gap='5' mb={4}>
          <Text color="gray.400">
            Total de claims: <Text as="span" color="white" fontWeight="bold">{count}</Text>
          </Text>
        </Flex>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          claims?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <Table variant="simple">
                    <Thead position="sticky" top={0} bg="gray.800" zIndex={1}>
                      <Tr>
                        <Th color="white">ID</Th>
                        <Th color="white">Usuario</Th>
                        <Th color="white">Email</Th>
                        <Th color="white">Posición (X, Z)</Th>
                        <Th color="white">Cheat Code</Th>
                        <Th color="white">Jugado el</Th>
                        <Th color="white">Reclamado el</Th>
                        <Th color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {claims.map((claim) => (
                        <Tr key={claim.id}>
                          <Td>{claim.userInfo.id}</Td>
                          <Td>{claim.userInfo.username}</Td>
                          <Td fontSize="sm">{claim.userInfo.email}</Td>
                          <Td>
                            ({claim.playInfo.xPosition.toFixed(2)}, {claim.playInfo.zPosition.toFixed(2)})
                          </Td>
                          <Td>
                            <Badge colorScheme={claim.playInfo.cheatCodeUsed ? "yellow" : "gray"}>
                              {claim.playInfo.cheatCodeUsed ? "Sí" : "No"}
                            </Badge>
                          </Td>
                          <Td fontSize="sm">{formatDate(claim.playInfo.playedAt)}</Td>
                          <Td fontSize="sm">{formatDate(claim.claimedAt)}</Td>
                          <Td>
                            <ClaimActions claim={claim} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay claims registrados para este premio</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />
      </Box>
    </Center>
  );
};

export default GameSeasonClawMachinePrizeClaims;
